import React, { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import axios from "axios";
import dropFileImg from "../../Assets/Icons/drop_file.png";
import uploadFileImg from "../../Assets/Icons/upload_file.png";
import { AppUrls } from "../app-urls";
import { useMsal } from "@azure/msal-react";
import ApiList from "./NextForm";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddProject = ({ onClose }) => {
    const { accounts } = useMsal();

    const [projectName, setProjectName] = useState("");
    const [subCategory, setSubCategory] = useState("");
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [shortDesc, setShortDesc] = useState("");
    const [longDesc, setLongDesc] = useState("");
    const [projectId, setProjectId] = useState("");
    const [projectImg, setProjectImg] = useState(null);
    const [projectImgUrl, setProjectImgUrl] = useState("");
    const [swaggerFile, setSwaggerFile] = useState(null);
    const [swaggerFileId, setSwaggerFileId] = useState("");
    const [swaggerApiData, setSwaggerApiData] = useState([]);
    const [userDetails, setUserDetails] = useState({
        publisherFirstName: "",
        publisherLastName: "",
        publisherEmail: "",
    });
    const [showApiListModal, setShowApiListModal] = useState(false);
    const [finalSwaggerId, setFinalSwaggerId] = useState("");

    useEffect(() => {
        if (accounts.length > 0) {
            const nameParts = accounts[0].name?.split(" ") || [];
            setUserDetails({
                publisherFirstName: nameParts[0] || "",
                publisherLastName: nameParts[1] || "",
                publisherEmail: accounts[0].username?.toLowerCase() || "",
            });
        }
    }, [accounts]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await axios.get(AppUrls.categoriesList);
                if (res.data?.data?.[0]?.categorylist) {
                    const categoryList = res.data.data[0].categorylist;
                    const categoryNames = categoryList?.map(category => category.name);
                    setSubCategoryList(categoryNames);
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
                toast.error("Error fetching categories.");
            }
        };
        fetchCategories();
    }, []);

    const uploadProjectLogo = async () => {
        if (!projectImg) {
            toast.error("Please select a project logo.");
            return;
        }
        const formData = new FormData();
        formData.append("fileData", projectImg);

        try {
            const res = await axios.put(AppUrls.projectLogo, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data?.projectLogoUrl) {
                setProjectImgUrl(res.data.projectLogoUrl);
                toast.success("Project logo uploaded successfully!");
            }
        } catch (error) {
            console.error("Error uploading project logo:", error);
            toast.error("Error uploading project logo.");
        }
    };

    const uploadSwagger = async () => {
        if (!swaggerFile) {
            toast.error("Please select a Swagger file.");
            return;
        }
        const formData = new FormData();
        formData.append("fileData", swaggerFile);

        try {
            const res = await axios.post(AppUrls.swaggerFile, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data?.data?.swaggerFileId) {
                setSwaggerFileId(res.data.data.swaggerFileId);
                setSwaggerApiData(res.data.data.apiIds);
                toast.success("Swagger file uploaded successfully!");
            }
        } catch (error) {
            console.error("Error uploading Swagger file:", error);
            toast.error("Error uploading Swagger file.");
        }
    };

    const submitFinalData = async () => {
        const reqBody = {
            projectName,
            category: [{ category: "Business Units", subCategory }],
            apiIds: swaggerApiData,
            shortDescription: shortDesc,
            longDescription: longDesc,
            assets: {
                files: { fileType: "swagger", fileId: swaggerFileId },
                projectLogoUrl: projectImgUrl,
            },
            status: { currentStatus: "incomplete" },
            publisherEmail: userDetails.publisherEmail,
            publisherFirstName: userDetails.publisherFirstName,
            publisherLastName: userDetails.publisherLastName,
        };

        // Validation
        if (
            !reqBody.projectName ||
            !reqBody.category[0].subCategory ||
            !reqBody.apiIds?.length ||
            !reqBody.shortDescription ||
            !reqBody.longDescription ||
            !reqBody.assets.files.fileId ||
            !reqBody.assets.projectLogoUrl ||
            !reqBody.publisherEmail ||
            !reqBody.publisherFirstName ||
            !reqBody.publisherLastName
        ) {
            toast.error("Please fill in all required fields before submitting.");
            return;
        }

        try {
            let res;
            if (projectId) {
                res = await axios.put(`${AppUrls.createProject}/detail/${projectId}`, reqBody);
                toast.success("Project updated successfully!");
            } else {
                res = await axios.put(AppUrls.createProject, reqBody);
                if (res.status === 201) {
                    toast.success("Project created successfully!");
                } else {
                    toast.error(res.message);
                }
            }

            if (res?.data?.projectId || projectId) {
                const idToSet = res.data?.projectId || projectId;
                setProjectId(idToSet);
                setFinalSwaggerId(swaggerFileId);
                setShowApiListModal(true);
            }
        } catch (error) {
            console.error("Error submitting project data:", error);
            toast.error(error.response?.data?.message || "An error occurred");
        }
    };


    const isSubmitDisabled = !projectImg || !swaggerFile;

    return (
        <div className="w-[80%]">
            {!showApiListModal ? (
                <div className="relative mx-auto p-6 bg-white rounded-lg shadow-lg overflow-y-auto max-h-[90vh]">
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 text-gray-600 hover:text-red-500"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    <h1 className="text-2xl font-bold mb-4">Add Project</h1>

                    <div className="border rounded p-4 flex items-center gap-4 bg-gray-50">
                        <img
                            src={projectImg ? URL.createObjectURL(projectImg) : dropFileImg}
                            alt="Project Logo"
                            className="w-12 h-12 rounded object-cover"
                        />
                        <div className="flex-1">
                            <p className="font-semibold">Drop file to upload or attach it (optional)</p>
                            <p className="text-sm text-gray-500">Maximum size: 500 x 500px, JPEG / PNG</p>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setProjectImg(e.target.files[0])}
                                className="mt-2"
                            />
                        </div>
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded"
                            onClick={uploadProjectLogo}
                        >
                            Upload
                        </button>
                    </div>

                    <div className="mt-4">
                        <input
                            type="text"
                            className="w-full border p-2 rounded mt-4"
                            placeholder="Project Name"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                        />
                        <div className="w-full rounded mt-4">
                            <label>Select Your Project BU</label>
                        </div>
                        <select
                            className="w-full border p-2 rounded mt-0"
                            value={subCategory}
                            onChange={(e) => setSubCategory(e.target.value)}
                        >
                            <option value="">Select Subcategory</option>
                            {subCategoryList?.map((sub, i) => (
                                <option key={i} value={sub.name ? sub.name : sub}>
                                    {sub.name ? sub.name : sub}
                                </option>
                            ))}
                        </select>

                        <textarea
                            className="w-full border p-2 rounded mt-4"
                            rows="3"
                            placeholder="Short Description"
                            value={shortDesc}
                            onChange={(e) => setShortDesc(e.target.value)}
                        />

                        <textarea
                            className="w-full border p-2 rounded mt-4"
                            rows="5"
                            placeholder="Long Description"
                            value={longDesc}
                            onChange={(e) => setLongDesc(e.target.value)}
                        />
                    </div>

                    <div className="mt-6 border rounded p-4 flex items-center gap-4 bg-gray-50">
                        <img
                            src={uploadFileImg}
                            alt="Swagger"
                            className="w-12 h-12 rounded object-cover"
                        />
                        <div className="flex-1">
                            <p className="font-semibold">Upload Swagger API File</p>
                            <p className="text-sm text-gray-500">Drop file to upload or click here to attach it</p>
                            <input
                                type="file"
                                accept="application/json"
                                onChange={(e) => setSwaggerFile(e.target.files[0])}
                                className="mt-2"
                            />
                        </div>
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded"
                            onClick={uploadSwagger}
                        >
                            Upload
                        </button>
                    </div>

                    <button
                        className={`mt-6 float-right ${isSubmitDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600'} text-white py-2 px-4 rounded hover:bg-blue-700 flex items-center justify-center gap-2`}
                        onClick={submitFinalData}
                        disabled={isSubmitDisabled}
                    >
                        {projectId ? "Update & Next" : "Next"} <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-[90%] p-6 relative h-[90vh] overflow-y-auto">

                        <ApiList
                            swaggerFileId={finalSwaggerId}
                            projectId={projectId}
                            setShowApiListModal={setShowApiListModal}
                            onClose={onClose}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddProject;
